import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { BookingQuestionsModal } from '../common/modals.js';
import BookingQuestionsForm from './bookingQuestionsForm.js';
import PolarSwitch from '../common/formComponents/polarSwitch';
import AsyncSelect from 'react-select/async';
import { SEC_ApiCall } from '../func.js';
import { CgSpinner } from 'react-icons/cg';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './bulkBookingModal.css';

/**
 * Modal used to edit the question responses for an existing booking.
 * Manage users can optionally reassign the booking to another staff member
 * when ``proxyAllowed`` is true.
 */
export default function EditBookingModal({
  modalActive,
  setModalActive,
  roomId,
  lessonId,
  roomName,
  date,
  questions = [],
  initialResponses = {},
  onComplete,
  proxyAllowed = false,
  proxyLocked = false,
  initialProxyUser = null
}) {
  const [responses, setResponses] = useState(initialResponses);
  const [proxyEnabled, setProxyEnabled] = useState(proxyLocked);
  const [proxyUser, setProxyUser] = useState(initialProxyUser);
  const [proxyOptions, setProxyOptions] = useState([]);
  const [proxyLoading, setProxyLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const displayDate = date ? moment(date, 'DDMMYYYY').format('dddd DD/MM/YYYY') : '';
  const displayLesson = lessonId ? lessonId.replace(/_/g, ' ') : '';

  useEffect(() => {
    setResponses(initialResponses);
  }, [initialResponses]);

  const loadProxyOptions = async (input = '') => {
    try {
      const params = input ? { q: input } : {};
      const res = await SEC_ApiCall('bookings/proxy/eligible-staff', params);
      return (res.items || []).map(i => ({ value: i.cogId, label: i.displayName }));
    } catch (e) {
      console.error('failed to load proxy staff', e);
      return [];
    }
  };

  useEffect(() => {
    if (proxyEnabled) {
      setProxyLoading(true);
      (async () => {
        try {
          let opts = await loadProxyOptions('');
          if (initialProxyUser && !opts.find(o => o.value === initialProxyUser.value)) {
            opts = [...opts, initialProxyUser];
          }
          if (opts.length === 0) {
            toast.error('An error has occurred getting available staff');
            setProxyEnabled(false);
          } else {
            setProxyOptions(opts);
            if (proxyLocked && initialProxyUser) {
              setProxyUser(initialProxyUser);
            }
          }
        } catch (err) {
          console.error('error loading proxy staff', err);
          toast.error('An error has occurred getting available staff');
          setProxyEnabled(false);
        } finally {
          setProxyLoading(false);
        }
      })();
    } else {
      setProxyOptions([]);
      if (!proxyLocked) {
        setProxyUser(null);
      }
    }
  }, [proxyEnabled, proxyLocked, initialProxyUser]);

  useEffect(() => {
    setResponses(initialResponses);
    setProxyEnabled(proxyLocked);
    setProxyUser(initialProxyUser);
  }, [initialResponses, proxyLocked, initialProxyUser, modalActive]);

  const handleSubmit = async () => {
    if (proxyEnabled && !proxyUser) {
      toast.error('⚠️ Please select a user to book for.', { autoClose: 4000, position: 'top-right' });
      return;
    }
    setSubmitting(true);
    const proxyFor =
      proxyEnabled && proxyUser && (!proxyLocked || proxyUser.value !== initialProxyUser?.value)
        ? proxyUser.value
        : null;
    await onComplete(JSON.stringify(responses), proxyFor);
    setSubmitting(false);
  };

  return (
    <BookingQuestionsModal modalActiveState={modalActive} setStateFunction={setModalActive}>
      <div className='bulkModalContent'>
        <h2 className='bulkTitle'>Edit Booking</h2>
        <p className='bulkSubTitle'>{roomName} - {displayLesson} - {displayDate}</p>
        {proxyAllowed && (
          <>
            <div className='optionRow'>
              <span className='optionLabel'>Proxy Booking</span>
              <PolarSwitch data={proxyEnabled} setData={setProxyEnabled} loading={proxyLoading} disabled={proxyLocked} />
            </div>
            {proxyEnabled && (
              <AsyncSelect
                cacheOptions
                defaultOptions={proxyOptions}
                loadOptions={loadProxyOptions}
                value={proxyUser}
                onChange={setProxyUser}
                className='proxySelect'
                placeholder='Select staff member'
              />
            )}
          </>
        )}
        {questions.length > 0 && (
          <BookingQuestionsForm
            questions={questions}
            initialResponses={initialResponses}
            onChange={setResponses}
            showSubmit={false}
          />
        )}
        <div className='submitArea'>
          <button
            className='formSubmitButton'
            type='button'
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? <CgSpinner className='submit-button-spinner' /> : 'Save'}
          </button>
        </div>
      </div>
    </BookingQuestionsModal>
  );
}
